import React, { useEffect, useRef } from 'react';

/**
 * Duração alvo, em segundos, da fase 1 da barra fake (o "disparo" de 0% → ~50%).
 * Mantido igual ao comportamento histórico de um vídeo de 15min (900s × 5% = 45s).
 * Espelhado em e-player-js/js/runner.js — mudar nos dois lugares.
 */
const PHASE1_TARGET_SECONDS = 45;

interface FakeProgressBarProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  currentTime: number;
  duration: number;
  color?: string;
  height?: number;
  hasBorder?: boolean;
  borderRadius?: number;
  borderWidth?: number;
}

export const FakeProgressBar: React.FC<FakeProgressBarProps> = ({
  videoRef,
  currentTime,
  duration,
  color = '#3b82f6',
  height = 12,
  hasBorder = false,
  borderRadius = 0,
  borderWidth = 2,
}) => {
  const progressRef = useRef<HTMLDivElement>(null);
  // Guarda o valor de "atual" da fase 1 para uso na fase 2 (espelhando runner.js:1944-1958)
  const fakePhase1Value = useRef<number>(0);

  // Mesma lógica de velocidade do player (runner.js — busque PHASE1_TARGET_SECONDS):
  // Fase 1: barra dispara de 0% → ~50%
  // Fase 2 (restante): barra completa de ~50% → 100% em velocidade reduzida
  useEffect(() => {
    if (progressRef.current && duration > 0) {
      let percentage: number;

      // Velocidade da fase 1. Era 10x fixo, o que fazia a fase 1 durar sempre
      // 5% da DURAÇÃO — 45s num vídeo de 15min, mas 90s em um de 30min e 180s
      // em um de 1h. Como a percepção do espectador é em tempo ABSOLUTO, em
      // vídeos longos a barra parecia parada e o gatilho de progresso sumia.
      // Derivando da própria condição de corte (currentTime * v = duration/2),
      // v = (duration/2) / PHASE1_TARGET_SECONDS mantém a fase 1 sempre em
      // ~45s. Em 15min isso dá exatamente 10, então a curva antiga é preservada
      // para vídeos curtos e a transição é contínua, sem degrau.
      const phase1Speed = Math.max(10, (duration * 0.5) / PHASE1_TARGET_SECONDS);

      if (currentTime * phase1Speed < duration * 0.5) {
        const progress = currentTime * phase1Speed;
        fakePhase1Value.current = progress;
        percentage = (progress / duration) * 100;
      } else {
        // Fase 2: usa o valor salvo da fase 1 como base
        const currentAtual = fakePhase1Value.current;
        const progress = currentAtual + currentTime + duration * 0.5;
        const maxValue = currentAtual + duration + duration * 0.5;
        percentage = (progress / maxValue) * 100;
      }

      progressRef.current.style.width = `${Math.min(100, percentage)}%`;
    }
  }, [currentTime, duration]);

  // Calcular offset baseado na borda
  const borderOffset = hasBorder ? borderWidth : 0;
  const bottomOffset = borderOffset > 0 ? `${borderOffset}px` : '0';
  const leftOffset = borderOffset > 0 ? `${borderOffset}px` : '0';
  const widthCalc = borderOffset > 0 ? `calc(100% - ${borderOffset * 2}px)` : '100%';

  // Calcular estilos baseado nas props
  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: bottomOffset,
    left: leftOffset,
    width: widthCalc,
    height: `${height}px`,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    overflow: 'hidden',
    zIndex: 5,
    ...(borderRadius > 0 && {
      borderRadius: `0 0 ${borderRadius}px ${borderRadius}px`,
    }),
  };

  const progressStyle: React.CSSProperties = {
    height: '100%',
    backgroundColor: color,
    width: '0%',
    transition: 'width 0.1s linear',
  };

  return (
    <div style={containerStyle}>
      <div ref={progressRef} style={progressStyle} />
    </div>
  );
};
